import React, { useEffect, useState, useMemo } from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import history from '../../../../history';
import TierDescriptionSavod from '../tierdescription/TierDescriptionSavod';
import DiscountedPriceNotification from '../discountedPriceNotification/DiscountedPriceNotification.loadable';
import Button, {
	BUTTON_KIND_TRANSPARENT,
	BUTTON_KIND_BRAND,
} from '../../../common/buttons/button/Button';
import {
	CURRENT,
	SUBSCRIPTION_TIER_B_TITLE,
	CANCEL,
	NOTIFICATION_CHANGE_TIERS_DISCOUNTED_PRICE,
	CHANGE_TIER_ACTION_BUTTON,
	BACK,
	SUBSCRIPTION_TIER_B_CHANGE_INFO,
	SUBSCRIPTION_TIER_B_CHANGE_INFO_SAVOD_1,
	SUBSCRIPTION_TIER_B_CHANGE_INFO_SAVOD_2,
	ONBOARDING_TIER_OPTIONS_SUBTITLE_PROMO_BENEFIT_B,
	CHANGE_TIER_FOOTNOTE,
} from '../../../../constants/Copy';
import {
	ROUTE_ACCOUNT_BEDROCK,
	ROUTE_CHANGE_TIER_REASON_BEDROCK,
} from '../../../../constants/Routes';
import {
	EVT_CHANGE_TIER_CANCEL,
	EVT_CHANGE_TIER_NOTIFICATION_DISCOUNTED_PRICE,
} from '../../../../constants/EventTypes';
import './ChangeTierSavod.scss';
import { isOnFreeTier } from '../../../../helpers/tiers';
import { useDispatch } from 'react-redux';
import { TIER_IDS, TIER_FREE } from '../../../../constants/Tiers';
import { changeTier as handleChangeTier } from '../../../../helpers/changeTier';
import { generatePath } from 'react-router';
import useSearchParams from '../../../../hooks/useSearchParams';
import { PARAM_NAMES } from '../../../../constants/Checkout';
import { useVideolandAccountState } from '../../../../hooks/useVideolandAccountState';
import { TIER_SWITCH_TYPES } from '../../../../constants/TierSwitch';
import { initiateTrackStructEvent } from '../../../analytics/Snowplow';
import {
	EVT_ACCOUNT,
	EVT_ELEMENT_INTERACTION,
	EVT_SUBSCRIPTION_CHANGE,
	EVT_ELEMENT_TYPE_BUTTON,
	EVT_SELECT,
	EVT_SELECT_SAVOD,
	EVT_SUBSCRIPTION_CHANGE_PROCEED,
	EVT_SUBSCRIPTION_CHANGE_PROCEED_SAVOD,
} from '../../../../constants/SnowplowEvents';
import { formatPrice } from '../../../../helpers/number';
import { getConnectedSavodTier } from '../../../../helpers/getConnectedSavodTier';
import TierList from '../tierdescription/TierList';
import { TierPresentationsC } from '../../../../constants/TierPresentations';
import ChangeTierModal from './ChangeTierModal';
import Error from '../../../common/error/Error';
import Spinner from '../../../common/spinner/Spinner';
import { getTrialPeriodText } from '../../../../helpers/sitespect';
import { setSavodSelectionData } from '../../../../actions/tierActions';

const ChangeTierSavod = (props) => {
	const {
		currentTier,
		tierCollection,
		savodCollection,
		isTiersPending,
		isTiersError,
		changeSubscriptionTier,
		analyticsPush,
	} = props;

	const [showChangeTierModal, setShowChangeTierModal] = useState(false);
	const tierCollectionFull = useMemo(() => [...tierCollection, TIER_FREE], [tierCollection]);
	const currentTierId = currentTier.currentPlan.planDescription.tierId;
	const activeSubscription = useSelector((state) => state.subscription.details?.data);
	const isTrustedParty = !!activeSubscription?.trustedParty;
	const subscriptionNumber = activeSubscription?.subscriptionNumber;
	const isUserOnFreeTier = isOnFreeTier(currentTierId);
	const { getSearchParam } = useSearchParams();
	const redirect = getSearchParam(PARAM_NAMES.REDIRECT_URL);
	const gigyaUser = useSelector((state) => state.authentication.gigyaUser);
	const savodSelection = useSelector((state) => state.tiers.savodSelection);
	const selectedTier = savodSelection?.selectedTier || currentTierId;
	const dispatch = useDispatch();

	const tierPresentation = useMemo(() => TierPresentationsC[selectedTier], [selectedTier]);
	const currentTierPresentation = useMemo(
		() => ({
			title: TierPresentationsC[currentTierId].title,
			price: savodCollection?.find((tier) => tier.id === currentTierId)?.pricing.recurring,
			label: TierPresentationsC[currentTierId].label,
		}),
		[currentTierId, savodCollection]
	);
	const selectedSavodTier = useMemo(
		() => getConnectedSavodTier(savodCollection, selectedTier),
		[savodCollection, selectedTier]
	);
	const { showPromo } = useVideolandAccountState();

	useEffect(() => {
		const isTierSwitchBlocked =
			isTrustedParty ||
			currentTier?.tierSwitch?.__typename !== TIER_SWITCH_TYPES.ALLOWED_TIER_SWITCH;
		if (isTierSwitchBlocked) {
			history.replace(generatePath(ROUTE_ACCOUNT_BEDROCK));
		}

		if (!savodSelection?.selectedTier) {
			dispatch(setSavodSelectionData(null, currentTierId, currentTierId));
		}
	}, [currentTierId, isTrustedParty, currentTier, savodSelection?.selectedTier, dispatch]);

	const isSelected = (tierId) => selectedTier === tierId || selectedTier === `${tierId}_WITH_ADS`;
	const confirmButtonDisabled =
		currentTierId === selectedTier && (currentTierId === 'FREE' || currentTierId === 'ESSENTIAL');

	const onTierChange = (tierId) => {
		dispatch(setSavodSelectionData(savodSelection?.savodTiers, tierId, currentTierId));
	};

	const changeTier = () => {
		handleChangeTier({
			selectedTier,
			currentTierId,
			tierCollection,
			gigyaUser,
			subscriptionNumber,
			showPromo,
			history,
			changeSubscriptionTier,
			analyticsPush,
			dispatch,
		});
	};

	const cancelChangeTier = () => {
		initiateTrackStructEvent({
			category: EVT_ACCOUNT,
			action: EVT_SUBSCRIPTION_CHANGE,
			label: EVT_SELECT,

			rtlElementContext: {
				elementInteraction: EVT_ELEMENT_INTERACTION,
				elementGroup: EVT_SUBSCRIPTION_CHANGE,
				elementType: EVT_ELEMENT_TYPE_BUTTON,
				elementName: EVT_SUBSCRIPTION_CHANGE_PROCEED,
				elementLabel: BACK,
			},
			rtlFunnel: {
				funnelName: EVT_SUBSCRIPTION_CHANGE,
				funnelStepName: 'Cancel New Subscription',
				funnelStep: '02',
				funnelSubscriptionType: selectedTier,
			},
			rtlUser: { rtlUserId: gigyaUser.UID },
			...(subscriptionNumber && {
				rtlPurchase: {
					videolandSubscriptionNumber: subscriptionNumber,
				},
			}),
		});

		analyticsPush({ label: CANCEL }, EVT_CHANGE_TIER_CANCEL);
		if (redirect) {
			window.history.back();
		} else {
			history.push(ROUTE_ACCOUNT_BEDROCK);
		}
	};

	if (isTiersPending) {
		return (
			<section className="change-tier-savod">
				<Spinner data-test="react-helper-spinner" />
			</section>
		);
	}

	if (isTiersError) {
		return (
			<section className="change-tier-savod">
				<Error />
			</section>
		);
	}

	const handleSelectNewTier = (isSavod = false) => {
		initiateTrackStructEvent({
			category: EVT_ACCOUNT,
			action: EVT_SUBSCRIPTION_CHANGE,
			label: isSavod ? EVT_SELECT_SAVOD : EVT_SELECT,

			rtlElementContext: {
				elementInteraction: EVT_ELEMENT_INTERACTION,
				elementGroup: EVT_SUBSCRIPTION_CHANGE,
				elementType: EVT_ELEMENT_TYPE_BUTTON,
				elementName: isSavod
					? EVT_SUBSCRIPTION_CHANGE_PROCEED_SAVOD
					: EVT_SUBSCRIPTION_CHANGE_PROCEED,
				elementLabel: CHANGE_TIER_ACTION_BUTTON,
			},
			rtlFunnel: {
				funnelName: EVT_SUBSCRIPTION_CHANGE,
				funnelStepName: 'Select New Subscription',
				funnelStep: isSavod ? '02b' : '02',
				funnelSubscriptionType: selectedTier,
			},
			rtlUser: { rtlUserId: gigyaUser.UID },
			...(subscriptionNumber && {
				rtlPurchase: {
					videolandSubscriptionNumber: subscriptionNumber,
				},
			}),
		});
		setShowChangeTierModal(true);
	};

	const doesTierSupportSavod = (tierId) => {
		const SAVOD_SUPPORTED_TIERS = [
			TIER_IDS.PREMIUM,
			TIER_IDS.PREMIUM_WITH_ADS,
			TIER_IDS.STANDARD,
			TIER_IDS.STANDARD_WITH_ADS,
		];
		return SAVOD_SUPPORTED_TIERS.includes(tierId);
	};

	const getTierConfiguration = (tierId) => {
		const tierConfigurations = {
			[TIER_IDS.PREMIUM]: { baseTierId: TIER_IDS.PREMIUM },
			[TIER_IDS.PREMIUM_WITH_ADS]: { baseTierId: TIER_IDS.PREMIUM },
			[TIER_IDS.STANDARD]: { baseTierId: TIER_IDS.STANDARD },
			[TIER_IDS.STANDARD_WITH_ADS]: { baseTierId: TIER_IDS.STANDARD },
		};

		return tierConfigurations[tierId];
	};

	const buildSavodTiersData = () => {
		if (!savodCollection?.length) {
			console.warn('SAVOD collection is empty or not loaded');
			return null;
		}

		const tierConfig = getTierConfiguration(selectedTier);
		if (!tierConfig) {
			console.warn(`No tier configuration found for: ${selectedTier}`);
			return null;
		}

		const baseTier = savodCollection.find((tier) => tier.id === tierConfig.baseTierId);
		if (!baseTier) {
			console.warn(`Base tier not found in collection: ${tierConfig.baseTierId}`);
			return null;
		}

		return {
			savodTier: selectedSavodTier,
			defaultTier: {
				id: baseTier.id,
				title: TierPresentationsC[tierConfig.baseTierId].title,
				price: baseTier.pricing.recurring,
				label: TierPresentationsC[tierConfig.baseTierId].label,
			},
		};
	};

	const navigateToSavodSelection = () => {
		const savodTiersData = buildSavodTiersData();

		if (!savodTiersData) {
			throw new Error('Failed to build SAVOD tiers data');
		}

		dispatch(setSavodSelectionData(savodTiersData, selectedTier, currentTierId));
		history.push(ROUTE_CHANGE_TIER_REASON_BEDROCK);
	};

	const handleConfirmTierChange = () => {
		try {
			if (doesTierSupportSavod(selectedTier)) {
				navigateToSavodSelection();
			} else {
				handleSelectNewTier();
			}
		} catch (error) {
			console.error('Tier change confirmation failed:', error);
		}
	};

	return (
		<section className="change-tier-savod">
			{tierPresentation && (
				<ChangeTierModal
					active={showChangeTierModal}
					setActive={setShowChangeTierModal}
					tierPresentation={tierPresentation}
					gigyaUser={gigyaUser}
					currentTierId={currentTierId}
					selectedTier={selectedTier}
					changeTier={changeTier}
					confirmButtonDisabled={confirmButtonDisabled}
					subscriptionNumber
				/>
			)}
			<>
				<header className="change-tier-savod__header">
					<DiscountedPriceNotification
						testId="change-tier-savod-discounted-price-warning"
						className="change-tier-savod__notification"
						copy={NOTIFICATION_CHANGE_TIERS_DISCOUNTED_PRICE}
						analyticsEventNameOnRender={EVT_CHANGE_TIER_NOTIFICATION_DISCOUNTED_PRICE}
					/>
					<h1 className="change-tier-savod__header--title">{SUBSCRIPTION_TIER_B_TITLE}</h1>
					<p className="change-tier-savod__header-description">
						<span className="change-tier-savod__header-description--label">{CURRENT}</span> Je kijkt
						op dit moment{' '}
						<span className="change-tier-savod__header-description--highlight">
							{currentTierPresentation.title}
						</span>{' '}
						voor{' '}
						<span className="change-tier-savod__header-description--highlight">
							{`€${formatPrice(currentTierPresentation.price)} per maand.`}
						</span>
					</p>
					{showPromo && (
						<div
							className="change-tier-savod__promo"
							/* eslint-disable-next-line react/no-danger*/
							dangerouslySetInnerHTML={{
								__html: getTrialPeriodText(ONBOARDING_TIER_OPTIONS_SUBTITLE_PROMO_BENEFIT_B),
							}}
						/>
					)}
				</header>
				<div
					className={classnames('change-tier-savod__tiers', {
						['change-tier-savod__tiers--free-tier']: isUserOnFreeTier,
					})}
				>
					{(isUserOnFreeTier ? tierCollectionFull : tierCollection).map((tier) => {
						const savodTier = getConnectedSavodTier(savodCollection, tier.id);
						return (
							<TierDescriptionSavod
								tierId={tier.id}
								pricing={formatPrice(tier.pricing.recurring)}
								key={tier.id}
								selected={isSelected(tier.id)}
								onClick={onTierChange}
								savodTier={savodTier}
							/>
						);
					})}
				</div>
				<div className="change-tier-savod__tier-table">
					<TierList tierFeatures={tierPresentation} />
				</div>

				<div className="change-tier-savod__info">
					{selectedSavodTier ? (
						<>
							<p>{SUBSCRIPTION_TIER_B_CHANGE_INFO_SAVOD_1}</p>
							<p>{SUBSCRIPTION_TIER_B_CHANGE_INFO_SAVOD_2}</p>
						</>
					) : (
						<p>{SUBSCRIPTION_TIER_B_CHANGE_INFO}</p>
					)}
				</div>
				<footer>
					<div className="change-tier-savod__footer-actions">
						<Button
							large
							data-test="change-tier-savod-cancel-button"
							kind={BUTTON_KIND_TRANSPARENT}
							onClick={cancelChangeTier}
						>
							{BACK}
						</Button>
						<Button
							large
							data-test="change-tier-confirm-button"
							kind={BUTTON_KIND_BRAND}
							onClick={handleConfirmTierChange}
							disabled={confirmButtonDisabled}
						>
							{CHANGE_TIER_ACTION_BUTTON}
						</Button>
					</div>
					<p className="change-tier-savod__footer-footnote">{CHANGE_TIER_FOOTNOTE}</p>
				</footer>
			</>
		</section>
	);
};

export default ChangeTierSavod;
